import { AgGridAngular } from "@ag-grid-community/angular";
import { ColDef, GridOptions, ICellRendererParams, ValueGetterParams } from "@ag-grid-community/core";
import { Component, computed, inject, signal, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccessLevel, AuthorizedAccess, OrganizationUser, UserApi } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import { AuthService, OrganizationAccountGroupService, OrganizationUsersService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  DropdownItem,
  IBadgeComponent,
  IDropdownComponent,
  ISelectComponent,
  ModalService,
  Option,
  ToasterService,
} from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { UserActivitiesGridComponent } from "@m19-board/settings/user-management/user-activities-grid/user-activities-grid.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import {
  GridDropdownComponent,
  GridDropdownParams,
} from "libs/m19-ui/src/components/grid-dropdown/grid-dropdown.component";
import { filter, switchMap, tap } from "rxjs";
import { IButtonComponent } from "../../../../../../libs/m19-ui/src/components/ibutton/ibutton.component";
import { ITabsComponent } from "../../../../../../libs/m19-ui/src/components/itabs/itabs.component";
import {
  AddAdminModalComponent,
  AddAdminModalData,
} from "./organization-card/add-admin-modal/add-admin-modal.component";
import {
  ManageAuthorizedAccessModalComponent,
  ManageAuthorizedAccessModalData,
  UserAccessMode,
} from "./organization-card/manage-authorized-user-modal/manage-authorized-access-modal.component";

enum UserManagementTab {
  USERS = "users",
  ACTIVITIES = "activities",
}

export enum UserRole {
  ADMIN = "Admin",
  OWNER = "Owner",
  USER = "User",
}

export type GridData = OrganizationUser & AuthorizedAccess;

export interface UserManagementData extends GridData {
  gridSection?: "admins" | "authorizedUsers";
  organizationName?: string;
  role?: UserRole;
  access?: number;
  isPending?: boolean;
}

@UntilDestroy()
@Component({
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  standalone: true,
  imports: [
    TranslocoDirective,
    AgGridAngular,
    UserActivitiesGridComponent,
    ISelectComponent,
    IButtonComponent,
    IDropdownComponent,
    ITabsComponent,
  ],
})
export class UserManagementComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly accountGroupService = inject(OrganizationAccountGroupService);
  private readonly organizationUsersService = inject(OrganizationUsersService);
  private readonly toasterService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly userApi = inject(UserApi);

  readonly AccessLevel = AccessLevel;
  readonly UserManagementTab = UserManagementTab;

  selectedTab = signal<UserManagementTab>(UserManagementTab.USERS);
  readonly tabs = [
    {
      label: this.translocoService.translate("common.users"),
      icon: "icon-[lucide--users]",
      value: UserManagementTab.USERS,
    },
    {
      label: this.translocoService.translate("page-title.activities"),
      icon: "icon-[lucide--activity]",
      value: UserManagementTab.ACTIVITIES,
    },
  ];

  readonly addUserOptions: DropdownItem[] = [
    {
      label: this.translocoService.translate("user-management.add_authorized_users"),
      onClick: () => {
        this.openAddNewAuthorizedUserModal();
      },
    },
    {
      label: this.translocoService.translate("user-management.add_admins"),
      onClick: () => {
        this.openAddAdminModal();
      },
    },
  ];

  user = toSignal(this.authService.loggedUser$);
  board = computed(() => this.user()?.board);
  allOrganizations = toSignal(this.accountGroupService.allOrganizationAccountGroups$);
  userOrganizations = toSignal(this.organizationUsersService.listOrganizations());
  authorizedAccess = toSignal(this.organizationUsersService.listAuthorizedAccess());

  selectedOrganization = signal<OrganizationAccountGroups | undefined>(this.allOrganizations()?.[0]);

  organizationOptions = computed<Option<OrganizationAccountGroups>[]>(
    () =>
      this.allOrganizations()?.map(
        (org): Option<OrganizationAccountGroups> => ({ value: org, label: org.organizationName }),
      ) ?? [],
  );

  private organizationOwnerAndAdmins = computed<UserManagementData[]>(() => {
    const data: UserManagementData[] = [];
    this.userOrganizations()?.forEach((user) => {
      const org = this.allOrganizations()?.find((org) => org.id === user.organizationId);
      if (!org) return;

      data.push({
        ...user,
        organizationName: org.organizationName,
        role: user.m19UserId === user.ownerId ? UserRole.OWNER : UserRole.ADMIN,
        isPending: !!user.invitedBy && !user.registered,
      });
    });
    return data;
  });

  private organizationAuthorizedUsers = computed<UserManagementData[]>(() => {
    const data: UserManagementData[] = [];
    const orgUser = new Set<string>();
    this.userOrganizations()?.forEach((user) => {
      const org = this.allOrganizations()?.find((org) => org.id === user.organizationId);
      if (!org) return;
      this.authorizedAccess()?.forEach((access) => {
        if (access.organizationId === org.id) {
          if (orgUser.has(access.organizationId + "_" + access.m19UserId)) return;
          data.push({
            ...access,
            organizationName: org.organizationName,
            role: UserRole.USER,
            isPending: !!access.invitedBy && !access.registered,
          });
          orgUser.add(access.organizationId + "_" + access.m19UserId);
        }
      });
    });
    return data;
  });

  private selectedOrganizationOwnerAndAdmins = computed<UserManagementData[]>(() => {
    return this.organizationOwnerAndAdmins().filter((user) => user.organizationId === this.selectedOrganization()?.id);
  });

  private selectedOrganizationAuthorizedUsers = computed<UserManagementData[]>(() => {
    return this.organizationAuthorizedUsers().filter((user) => user.organizationId === this.selectedOrganization()?.id);
  });

  gridData = computed<UserManagementData[]>(() => {
    return [...this.selectedOrganizationOwnerAndAdmins(), ...this.selectedOrganizationAuthorizedUsers()];
  });

  private selectedOrganizationAccessByUserId = computed<Map<number, AuthorizedAccess[]>>(() => {
    const map = new Map<number, AuthorizedAccess[]>();
    this.authorizedAccess()
      ?.filter((access) => access.organizationId === this.selectedOrganization()?.id)
      .forEach((access) => {
        map.set(access.m19UserId!, [...(map.get(access.m19UserId!) ?? []), access]);
      });
    return map;
  });

  private userAccessEmails = computed<string[]>(() => {
    return Array.from(this.selectedOrganizationAccessByUserId().values())
      .flatMap((access) => access.map((a) => a.email))
      .filter((email): email is string => email !== undefined);
  });

  private adminsEmails = computed<string[]>(() => {
    return this.selectedOrganizationOwnerAndAdmins()
      .map((user) => user.email)
      .filter((email): email is string => email !== undefined);
  });

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    suppressMovable: true,
    cellClass: "flex items-center",
  };

  private columnDefs: ColDef<UserManagementData>[] = [
    {
      field: "userName",
      flex: 1,
      headerName: this.translocoService.translate("common.name"),
      filter: "agTextColumnFilter",
    },
    {
      field: "email",
      flex: 1,
      headerName: this.translocoService.translate("manage-authorized-access-modal.email"),
      filter: "agTextColumnFilter",
      cellClass: "text-gray-600 font-medium",
    },
    {
      field: "invitedBy",
      headerName: "Status",
      width: 120,
      valueGetter: (params: ValueGetterParams<UserManagementData>) => {
        return this.translocoService
          .translate(params.data?.isPending ? "user-management.pending" : "user-management.accepted")
          .toUpperCase();
      },
      valueFormatter: (_) => {
        return ""; // hide value if user is not pending
      },
      filter: "agSetColumnFilter",
      cellRendererSelector: (params: ICellRendererParams<UserManagementData>) => {
        if (params.data?.isPending) {
          const lastInviteDate = new Date(params.data.lastInvite!);
          const lastInviteDateString = lastInviteDate.toLocaleDateString(this.user()?.locale ?? "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          return {
            component: IBadgeComponent,
            params: {
              icon: "icon-[lucide--mail]",
              label: this.translocoService.translate("user-management.pending").toUpperCase(),
              color: "orange",
              size: "sm",
              variant: "soft",
              tooltipValue: this.translocoService.translate("user-management.last_invite_sent", [lastInviteDateString]),
            },
          };
        }
        return undefined;
      },
    },
    {
      field: "role",
      headerName: this.translocoService.translate("user-management.role"),
      width: 120,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [UserRole.ADMIN, UserRole.OWNER, UserRole.USER],
      },
      cellClass: "text-gray-600 font-medium",
    },
    {
      colId: "actions",
      flex: 1,
      headerName: "",
      filter: false,
      floatingFilter: false,
      cellClass: "flex items-center justify-end",
      cellRendererSelector: (params: ICellRendererParams<UserManagementData>) => {
        const userData = params.node?.data;
        const items: DropdownItem[][] = [[]];
        if (userData?.isPending) {
          items[0].push({
            icon: "icon-[lucide--mail]",
            label: this.translocoService.translate("user-management.resend_invite"),
            onClick: () => this.resendInvite(userData),
          });
        }

        if (userData?.role === UserRole.USER) {
          items[0].push({
            icon: "icon-[lucide--settings]",
            disabled: !this.selectedOrganization()?.hasActiveSubscription(),
            label: this.translocoService.translate("account-setting.manage_access"),
            onClick: () => this.openManageAuthorizedUserModal(userData),
          });
          items[0].push({
            icon: "icon-[lucide--x]",
            disabled: !this.selectedOrganization()?.hasSubscription(),
            label: this.translocoService.translate("user-management.revoke"),
            onClick: () => this.revokeAuthorizedAccess(userData),
            variant: "danger",
          });
        }

        if (
          userData?.role === UserRole.ADMIN &&
          userData?.ownerId !== userData?.m19UserId &&
          userData?.m19UserId !== this.user()?.userId
        ) {
          items[0].push({
            icon: "icon-[lucide--x]",
            disabled: !this.selectedOrganization()?.hasSubscription(),
            label: this.translocoService.translate("user-management.revoke"),
            onClick: () => this.revokeAdminAccess(userData),
            variant: "danger",
          });
        }

        return items[0].length > 0
          ? {
              component: GridDropdownComponent,
              params: {
                icon: "icon-[lucide--more-horizontal]",
                items: items,
              } as GridDropdownParams,
            }
          : undefined;
      },
    },
  ];

  gridOptions: GridOptions<UserManagementData> = {
    defaultColDef: this.defaultColDef,
    suppressRowHoverHighlight: true,
    suppressMultiRangeSelection: true,
    sideBar: false,
    columnDefs: this.columnDefs,
    enableRangeSelection: false,
    getRowId: (params) => params.data.organizationId + "_" + params.data.m19UserId + "_" + params.data.role,
  };

  constructor() {
    // old UI
    localStorage.removeItem("user-management-grid_gridConfig");
    localStorage.removeItem("userManagementGrid_gridConfig");
  }

  private revokeAdminAccess(admin: UserManagementData): void {
    const org = this.selectedOrganization();
    if (!org) return;

    const message = admin.isPending
      ? this.translocoService.translate("user-management.cancel_invite_confirmation", [admin.email])
      : this.translocoService.translate("user-management.revoke_admin_confirmation", [
          admin.userName ?? "",
          admin.email,
          org.organizationName,
        ]);
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("user-management.revoke"),
      data: {
        message,
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.toggleGridLoading(true)),
        switchMap(() => this.organizationUsersService.revokeAdminAccessV2(admin.organizationId!, admin.m19UserId!)),
      )
      .subscribe({
        next: () => {
          const message = admin.isPending
            ? this.translocoService.translate("user-management.cancel_invite_success", [admin.email])
            : this.translocoService.translate("user-management.revoke_admin_success", [
                admin.email,
                org.organizationName,
              ]);
          this.toasterService.success(message);
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error.response.message);
          this.toggleGridLoading(false);
        },
      });
  }

  openManageAuthorizedUserModal(user: UserManagementData): void {
    const org = this.selectedOrganization();

    if (!org || !org.hasActiveSubscription()) return;

    const email = user.email;
    const userName = user.userName;
    const authorizedAccess = new Map(
      this.selectedOrganizationAccessByUserId()
        .get(user.m19UserId!)!
        .map((x) => [x.profileId, x]),
    );
    const data: ManageAuthorizedAccessModalData = {
      accessMode: UserAccessMode.UPDATE_ACCESS,
      userEmail: email,
      userName: userName!,
      board: this.board()!,
      initialAuthorizedOrganizationProfileIds: authorizedAccess,
      organizationId: org.id,
      organizationName: org.organizationName,
    };
    this.modalService.openModal<ManageAuthorizedAccessModalData, void>(ManageAuthorizedAccessModalComponent, {
      modalTitle: this.translocoService.translate("manage-authorized-access-modal.authorized_user", {
        name: org.organizationName,
        new: false,
      }),
      data,
    });
  }

  private revokeAuthorizedAccess(user: UserManagementData): void {
    const email = user.email;
    const access = this.selectedOrganizationAccessByUserId().get(user.m19UserId!)!;

    const message = this.translocoService.translate("user-management.revoke_user_confirmation", [
      user.userName ?? "",
      user.email,
      this.selectedOrganization()?.organizationName,
    ]);
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("user-management.revoke"),
      data: {
        message,
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.toggleGridLoading(true)),
        switchMap(() => this.organizationUsersService.revokeAuthorizedAccessAsync(access)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            "Successfully revoked access from " + email + " to " + access.length + " accounts",
            "Admin access revoked",
          );
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error, "Admin access revocation error");
          this.toggleGridLoading(false);
        },
      });
  }

  private openAddAdminModal(): void {
    const org = this.selectedOrganization();
    if (!org || (!org.hasActiveSubscription() && !org.parentOrganizationWhenOnwer)) return;

    this.modalService.openModal<AddAdminModalData, void>(AddAdminModalComponent, {
      modalTitle: this.translocoService.translate("add-admin-modal.add_admin"),
      data: {
        organizationId: org.id,
        organizationName: org.organizationName,
      },
    });
  }

  private openAddNewAuthorizedUserModal(): void {
    const org = this.selectedOrganization();

    if (!org || !org.hasActiveSubscription()) return;

    const data: ManageAuthorizedAccessModalData = {
      accessMode: UserAccessMode.ADD_ACCESS,
      board: this.board()!,
      organizationId: org.id,
      organizationName: org.organizationName,
      adminsEmails: this.adminsEmails(),
      authorizedUserEmails: this.userAccessEmails(),
    };
    this.modalService.openModal<ManageAuthorizedAccessModalData, void>(ManageAuthorizedAccessModalComponent, {
      modalTitle: this.translocoService.translate("manage-authorized-access-modal.authorized_user", {
        name: org.organizationName,
        new: true,
      }),
      data,
    });
  }

  private toggleGridLoading(loading: boolean): void {
    if (loading) {
      this.agGrid.api.showLoadingOverlay();
    } else {
      this.agGrid.api.hideOverlay();
    }
  }

  private resendInvite(user: UserManagementData): void {
    if (!user.isPending || !user.email || !this.selectedOrganization()?.id) return;

    this.toggleGridLoading(true);
    this.userApi
      .sendUserInvitation({
        email: user.email,
        domain: window.location.hostname,
        organizationId: this.selectedOrganization()!.id!,
      })
      .pipe(catchAjaxError())
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("user-management.invite_resent", [user.email]));
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error, "Invite resend error");
          this.toggleGridLoading(false);
        },
      });
  }
}
